import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Constants } from 'src/app/shared/constants/constants.constant';
import { ClickFocusTrapDirective } from 'src/app/shared/directives/click-focus-trap.directive';
import { AddedUpdatedDetail } from 'src/app/shared/models/details/added-updated-detail.model';
import { FormControl as FrmCntl } from 'src/app/shared/models/forms';
import { AssetLookupValue, LookupValue } from 'src/app/shared/models/lookups';
import { AddedUpdatedDetailsService } from 'src/app/shared/services/added-updated-details.service';
import { WorkActivityLookupService } from 'src/app/shared/services/work-activity-lookup.service';
import { BaseComponent } from 'src/app/core/components/base.component';
import { AppContextService } from 'src/app/core/services/app-context.service';
import { TooltipMessageService } from 'src/app/core/services/tooltip-message.service';
import { CustomValidators } from 'src/app/core/utils';
import { AccountAdminConstants } from '../../../shared/constants';
import { UserProfile } from '../../../shared/models';
import { ExternalUserProfileApiService } from '../../../shared/services/external-user-profile-api.service';

@Component({
	selector: 'app-external-user-profile-details',
	templateUrl: './external-user-profile-details.component.html',
	standalone: false,
	hostDirectives: [ClickFocusTrapDirective],
})
export class ExternalUserProfileDetailsComponent extends BaseComponent implements OnInit {
	@Input() item: UserProfile = new UserProfile();
	@Output() editCancelled = new EventEmitter();
	@Output() detailsUpdated = new EventEmitter();

	formControls = FrmCntl;
	isCurrentUser = false;
	isClientUser = false;
	isBusinessEntityUser = false;
	isCustomerUser = false;

	userRoles: LookupValue[] = [];
	statuses: LookupValue[] = [];

	initialValues: any;
	RecordTypeConstants = Constants.RecordType;
	addedUpdatedDetails: AddedUpdatedDetail;
	showResetLoginDialog = false;
	resetLoginConfirmationText = '';

	detailForm = this.fb.group({
		username: ['', [Validators.required, CustomValidators.email]],
		displayName: ['', [Validators.required]],
		contactPhone: ['', [CustomValidators.phone('Phone #')]],
		contactMobile: ['', [CustomValidators.phone('Mobile #')]],
		contactEmail: ['', [Validators.required, CustomValidators.email]],
		mobileNumber: ['', [CustomValidators.e164Mobile]],
		userRelationType: [null],
		userRole: [null, [Validators.required]],
		status: [null, [Validators.required]],
		clientId: [null],
		clientName: [null],
		businessEntityId: [null],
		businessEntityName: [null],
		linkedBusinessEntityId: [null],
		linkedBusinessEntityName: [null],
		assets: [null],
	});

	constructor(
		appContext: AppContextService,
		private externalUserProfileApiService: ExternalUserProfileApiService,
		private fb: UntypedFormBuilder,
		private activatedRoute: ActivatedRoute,
		private tooltipMessageService: TooltipMessageService,
		private addedUpdatedDetailsService: AddedUpdatedDetailsService,
		public workActivityLookupService: WorkActivityLookupService
	) {
		super(appContext);
	}

	ngOnInit(): void {
		this.activatedRoute.data.subscribe(data => {
			this.item = data.pageData.user;
			this.workActivityLookupService.selectedClientId = this.item.ClientId ?? null;
			this.isCurrentUser = this.item.IsCurrentUser;
			this.isClientUser = this.item.UserRelationTypeCode === Constants.UserRelationType.Client;
			this.isBusinessEntityUser = this.item.UserRelationTypeCode === Constants.UserRelationType.Contractor;
			this.isCustomerUser = this.item.UserRelationTypeCode === Constants.UserRelationType.Customer;
			this.initLookups(data);
			this.patchForm();
			this.initialValues = this.detailForm.value;
		});
	}

	initLookups(data) {
		this.userRoles = (this.isClientUser ? data.pageData.clientUserRoles : this.isBusinessEntityUser ? data.pageData.contractorUserRoles : []).slice();
		this.statuses = data.pageData.statuses.slice();

		this.util.addRecordValueIdIntoLookup(this.item.UserRoleId, this.item.UserRoleName, this.userRoles);
		this.util.addRecordValueIntoLookup(this.item.StatusCode, this.item.StatusName, this.statuses);
	}

	refreshData() {
		this.externalUserProfileApiService.getUserProfile(this.item.UserProfileId).subscribe(resp => {
			this.item = resp;
			this.patchForm();

			this.detailsUpdated.emit(this.item);
		});
	}

	patchForm() {
		const userRole = this.userRoles.find(x => x.LookupValueId === this.item.UserRoleId);
		const status = this.statuses.find(x => x.CodeValue === this.item.StatusCode);

		const selectedAssets = (this.item.AssetRestrictions ?? []).map(x => {
			return new AssetLookupValue({
				LookupValueId: x.LookupValueId,
				NameValue: x.DisplayName ?? x.NameValue,
				CodeValue: null,
			});
		});

		this.detailForm.patchValue({
			username: this.item.Username,
			displayName: this.item.DisplayName,
			status: status,
			userRelationType: this.item.UserRelationTypeName,
			userRole: userRole,
			contactPhone: this.item.ContactPhone,
			contactMobile: this.item.ContactMobile,
			contactEmail: this.item.ContactEmail,
			mobileNumber: this.item.MobileNumber,
			clientId: this.item.ClientId,
			clientName: this.item.ClientName,
			businessEntityId: this.item.BusinessEntityId,
			businessEntityName: this.item.BusinessEntityName,
			linkedBusinessEntityId: this.item.LinkedUserBusinessEntityId,
			linkedBusinessEntityName: this.item.LinkedUserBusinessEntityName,
			assets: selectedAssets.length > 0 ? selectedAssets.slice() : null,
		});
		this.addedUpdatedDetails = this.addedUpdatedDetailsService.createAddedUpdatedDetailsObject(this.item);
	}

	editDetails() {
		this.detailForm.reset();
		this.patchForm();
		this.initialValues = this.detailForm.value;
		this.formMode = this.formModes.Edit;
	}

	cancelDetailsEdit() {
		this.util.clearMessages();
		this.detailForm.reset(this.initialValues);

		if (this.formMode != this.formModes.Create) {
			this.formMode = this.formModes.View;
		}

		this.editCancelled.emit();
	}

	saveDetails() {
		this.util.clearMessages();
		this.appContext.loaderService.block();

		if (this.detailForm.valid) {
			const updatedObj = this.util.deepClone(this.item);
			updatedObj.DisplayName = this.detailForm.value.displayName;
			updatedObj.StatusCode = this.detailForm.value.status?.CodeValue;
			updatedObj.StatusName = this.detailForm.value.status?.NameValue;
			updatedObj.UserRoleId = this.detailForm.value.userRole?.LookupValueId;
			updatedObj.UserRoleName = this.detailForm.value.userRole?.NameValue;
			updatedObj.ContactPhone = this.detailForm.value.contactPhone;
			updatedObj.ContactMobile = this.detailForm.value.contactMobile;
			updatedObj.ContactEmail = this.detailForm.value.contactEmail;
			updatedObj.MobileNumber = this.detailForm.value.mobileNumber;
			updatedObj.AssetRestrictions = this.util.getRecordValueId(this.detailForm.value.assets);

			this.externalUserProfileApiService.saveUserProfile(updatedObj).subscribe(result => {
				if (result.IsSuccess) {
					this.item = this.util.deepClone(result.Data);
					this.detailForm.reset();
					this.patchForm();
					this.initialValues = this.detailForm.value;
					this.formMode = this.formModes.View;

					this.detailsUpdated.emit(this.item);
				}
				this.appContext.loaderService.unblock();
			});
		} else {
			this.appContext.loaderService.unblock();
			this.markGroupDirty(this.detailForm);
		}
	}

	sendInvitation() {
		this.appContext.loaderService.block();
		this.externalUserProfileApiService.sendInvitation(this.item.UserProfileId).subscribe(result => {
			this.appContext.loaderService.unblock();
			if (result.IsSuccess) {
				this.appContext.util.showSuccess('Sent successfully');
			}
		});
	}

	private resetUserB2C() {
		this.appContext.loaderService.block();
		this.externalUserProfileApiService.resetUserB2C(this.item.UserProfileId).subscribe(result => {
			this.appContext.loaderService.unblock();
			if (result.IsSuccess) {
				this.appContext.util.showSuccess('Login method reset successfully');
			}
		});
	}

	async resetLogin() {
		const proceed = await this.util.confirmQuestion('Do you want to proceed to reset your login method?', 'Confirm Reset Login');
		if (!proceed) {
			return;
		}

		this.resetLoginConfirmationText = '';
		this.showResetLoginDialog = true;
	}

	confirmResetLogin() {
		if (this.resetLoginConfirmationText?.trim() !== 'RESET') {
			this.util.showWarning('Reset login cancelled. Confirmation text did not match.');
			return;
		}

		this.showResetLoginDialog = false;
		this.resetLoginConfirmationText = '';
		this.resetUserB2C();
	}

	cancelResetLogin() {
		this.showResetLoginDialog = false;
		this.resetLoginConfirmationText = '';
	}

	showEdit(): boolean {
		return this.item?.ObjectAccess?.AllowEdit?.Hide != true;
	}

	allowEdit(): boolean {
		return this.item?.ObjectAccess?.AllowEdit?.IsAllowed;
	}

	getEditTooltip(): string {
		return (
			this.tooltipMessageService.getTooltipMessage('C_EXTERNALUSER', 'AllowEdit', this.item?.ObjectAccess?.AllowEdit, this.item) ??
			AccountAdminConstants.Tab_detail_EditExternalUserProfile
		);
	}

	showSendInvitation(): boolean {
		return this.appContext.hasPermission(Constants.UserPermission.ExternalUserUpdate) && this.item.StatusCode === Constants.UserProfileStatus.Invited;
	}

	showResetLogin(): boolean {
		return this.isCurrentUser && this.formMode === this.formModes.View;
	}
}
